import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Truck, Lock, RefreshCw, MapPin, Store, User, Route as RouteIcon, FileCheck } from "lucide-react";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";

// status logistico -> [rotulo, classe]
const ST = {
  WAITING_SIGNATURE: ["🔒 Entrega bloqueada", "bg-red-50 text-red-700"],
  READY_FOR_DELIVERY: ["✅ Liberada para entrega", "bg-emerald-50 text-emerald-700"],
  ROUTE_ASSIGNED: ["📦 Rota atribuída", "bg-amber-50 text-amber-700"],
  OUT_FOR_DELIVERY: ["🚚 Em entrega", "bg-amber-50 text-amber-700"],
  ARRIVED: ["📍 Chegou", "bg-amber-50 text-amber-700"],
  DELIVERED: ["✅ Entregue", "bg-blue-50 text-blue-700"],
  DELIVERY_FAILED: ["⚠️ Falhou", "bg-orange-50 text-orange-700"],
  RETURNED: ["↩️ Devolvida", "bg-gray-100 text-gray-600"],
  CANCELLED: ["✖ Cancelada", "bg-gray-100 text-gray-500"],
};

// filtros da fila (agrupa "Em rota")
const VIEWS = [
  ["", "Todas"],
  ["CRIS_LINGERIE", "Cris Lingerie"],
  ["ELETROS", "Eletros"],
  ["WAITING_SIGNATURE", "Bloqueadas"],
  ["READY_FOR_DELIVERY", "Liberadas"],
  ["IN_ROUTE", "Em rota"],
  ["DELIVERED", "Entregues"],
  ["CANCELLED", "Canceladas"],
];
const IN_ROUTE = ["ROUTE_ASSIGNED", "OUT_FOR_DELIVERY", "ARRIVED"];
const UNITS = ["CRIS_LINGERIE", "ELETROS"];

const addr = (a) => (a ? [a.street, a.number, a.district, a.city].filter(Boolean).join(", ") : "—");

function sigInfo(o) {
  if (o.delivery_status === "WAITING_SIGNATURE") return ["Aguardando assinatura", "text-red-600"];
  if (o.signature_required) return ["Promissória assinada", "text-emerald-600"];
  return ["Sem promissória", "text-[#9A8A98]"];
}

export default function LogisticaDeliveries() {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    api.get("/deliveries").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/deliveries/sync");
      toast.success(`Sincronizado: ${data.created} nova(s), ${data.already_exists} já existente(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao sincronizar"); }
    finally { setSyncing(false); }
  };

  const openReceipt = async (id) => {
    try {
      const { data } = await api.get(`/deliveries/${id}/proof/receipt`, { responseType: "text" });
      const w = window.open("", "_blank");
      if (w) { w.document.write(data); w.document.close(); }
    } catch { toast.error("Comprovante indisponível"); }
  };

  const filtered = (rows || []).filter((o) => {
    if (!view) return true;
    if (UNITS.includes(view)) return o.business_unit === view;
    if (view === "IN_ROUTE") return IN_ROUTE.includes(o.delivery_status);
    return o.delivery_status === view;
  });

  return (
    <div className="space-y-4 p-4 lg:p-8" data-testid="rota-deliveries-page">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #B5179E)` }}>
        <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: GOLD }}><Truck size={22} className="text-[#2B1E4A]" /></span>
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>CRIS ROTA</div>
          <h1 className="font-display text-xl font-bold">Fila de Entregas</h1>
        </div>
        <button onClick={sync} disabled={syncing} className="flex items-center gap-1.5 rounded-xl border border-white/30 bg-white/10 px-3 py-2 text-[12px] font-bold text-white disabled:opacity-50" data-testid="rota-sync-btn">
          <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Sincronizando…" : "Sincronizar vendas"}
        </button>
      </div>

      <p className="text-[12px] text-[#8A7788]">As entregas são criadas automaticamente a partir das vendas Cris Lingerie e Eletros. Use "Sincronizar vendas" apenas para reconciliar entregas ausentes.</p>

      <div className="flex flex-wrap gap-2" data-testid="rota-filters">
        {VIEWS.map(([val, label]) => (
          <button key={val || "all"} onClick={() => setView(val)}
            className={`rounded-full px-3 py-1.5 text-[12px] font-bold transition ${view === val ? "text-white" : "border border-border text-[#6B5A69]"}`}
            style={view === val ? { background: PURPLE } : {}}
            data-testid={`rota-filter-${val || "all"}`}>{label}</button>
        ))}
      </div>

      {rows === null ? <div className="p-8 text-center text-sm text-[#9A8A98]">Carregando...</div> :
        filtered.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-[#6B5A69]" data-testid="rota-empty">Nenhuma entrega nesta visão. Crie uma venda ou clique em "Sincronizar vendas".</div> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="rota-deliveries-list">
          {filtered.map((o) => {
            const [label, cls] = ST[o.delivery_status] || ["—", "bg-gray-100"];
            const [sigLabel, sigCls] = sigInfo(o);
            const blocked = o.delivery_status === "WAITING_SIGNATURE";
            return (
              <div key={o.id} className={`rounded-2xl border bg-white p-4 ${blocked ? "border-red-200" : "border-border"}`} data-testid={`rota-delivery-${o.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-display font-bold text-brand-dark">{o.customer_name || "Cliente"}</div>
                    <div className="text-[11px] uppercase tracking-wide" style={{ color: o.business_unit === "ELETROS" ? "#C9960A" : "#5B2CA0" }}>{o.business_unit === "ELETROS" ? "Eletros" : "Cris Lingerie"}</div>
                  </div>
                  <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid={`rota-status-${o.id}`}>{label}</span>
                </div>

                <div className="mt-2 flex items-start gap-1 text-[12px] text-[#8A7788]"><MapPin size={13} className="mt-0.5 shrink-0" /> {addr(o.delivery_address_snapshot)}</div>

                <div className="mt-2 space-y-1 text-[11px] text-[#6B5A69]">
                  <div className="flex items-center gap-1"><Store size={12} /> Revendedora: <span className="font-semibold">{o.reseller_name || "—"}</span></div>
                  <div className="flex items-center gap-1"><User size={12} /> Entregador: <span className="font-semibold">{o.driver_name || "—"}</span></div>
                  {o.route_id && <div className="flex items-center gap-1"><RouteIcon size={12} /> Rota: <span className="font-semibold">{o.route_name || "atribuída"}</span></div>}
                  <div>Assinatura: <span className={`font-semibold ${sigCls}`}>{sigLabel}</span></div>
                </div>

                {blocked && (
                  <div className="mt-2 flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-[11px] font-bold text-red-700"><Lock size={12} /> Promissória aguardando assinatura</div>
                )}
                {o.delivery_status === "DELIVERED" && (
                  <button onClick={() => openReceipt(o.id)} className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-blue-200 py-1.5 text-[12px] font-bold text-blue-700" data-testid={`rota-receipt-${o.id}`}><FileCheck size={13} /> Ver comprovante</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
